import {
  Button,
  Typography,
  ActionConfirmationModal,
  ServerErrorFallbackCard,
} from "@/components/ui";
import { Plus, Building2, User, Trash2 } from "lucide-react";
import { type ContactTypeFromZod } from "@/lib/data/schemas/contact/ContactSchema";
import {
  type ContactConnection,
  type ContactRelatedConnectionType,
} from "@/lib/data/schemas/contact/ContactSchema";
import {
  flattenChildConnections,
  flattenRelatedConnections,
} from "@/lib/utils/contact-connections";
import { formatContactsName } from "@/lib/utils/format";
import {
  useCreateContactConnection,
  useDeleteContactConnection,
} from "@/lib/data/react-query/contacts/contact-connection";
import SelectContactModal from "../SelectContactModal/SelectContactModal";
import { useState } from "react";
import type { Contact } from "@/lib/types";
import { ErrorBoundary } from "@sentry/react";
import { useNavigate } from "@tanstack/react-router";

interface ContactConnectionsSectionProps {
  contact: ContactTypeFromZod;
  hasCreatePermission?: boolean;
  hasDeletePermission?: boolean;
}

type ConnectionType = "parent" | "child" | "related" | null;

interface ConnectionToDelete {
  id: string;
}

type ConnectionWithContact = ContactConnection & {
  contact: NonNullable<ContactConnection["contact"]>;
};

function ContactConnectionsSectionDangerous({
  contact,
  hasCreatePermission = false,
  hasDeletePermission = false,
}: ContactConnectionsSectionProps) {
  const navigate = useNavigate();
  const [slideoverOpen, setSlideoverOpen] = useState(false);
  const [connectionType, setConnectionType] = useState<ConnectionType>(null);
  const [connectionToDelete, setConnectionToDelete] =
    useState<ConnectionToDelete | null>(null);
  const createContactConnection = useCreateContactConnection();
  const deleteContactConnection = useDeleteContactConnection();

  const parentConnections: ConnectionWithContact[] =
    contact.parentConnections.filter(
      (connection): connection is ConnectionWithContact =>
        connection.contact !== null,
    );
  const childConnections: ConnectionWithContact[] = flattenChildConnections(
    contact.childConnections,
  ).filter(
    (connection): connection is ConnectionWithContact =>
      connection.contact !== null,
  );
  const relatedConnections: Array<
    ContactRelatedConnectionType & ConnectionWithContact
  > = flattenRelatedConnections(contact.relatedConnections).filter(
    (
      connection,
    ): connection is ContactRelatedConnectionType & ConnectionWithContact =>
      connection.contact !== null,
  );

  // Get all connected contact IDs to exclude from selection
  const connectedContactIds = [
    ...parentConnections.map((conn) => conn.contact.id),
    ...childConnections.map((conn) => conn.contact.id),
    ...relatedConnections.map((conn) => conn.contact.id),
    contact.id, // Exclude self
  ];

  const handleAddParentConnection = () => {
    setConnectionType("parent");
    setSlideoverOpen(true);
  };

  const handleAddChildConnection = () => {
    setConnectionType("child");
    setSlideoverOpen(true);
  };

  const handleAddRelatedConnection = () => {
    setConnectionType("related");
    setSlideoverOpen(true);
  };

  const handleSelectContact = (selectedContact: Contact) => {
    if (!connectionType) return;

    if (connectionType === "parent") {
      // This contact reports to the selected contact
      createContactConnection.mutate({
        sourceContactId: contact.id,
        targetContactId: selectedContact.id,
        relationshipType: "reports_to",
      });
    } else if (connectionType === "child") {
      // Child: The selected contact reports to this contact
      createContactConnection.mutate({
        sourceContactId: selectedContact.id,
        targetContactId: contact.id,
        relationshipType: "reports_to",
      });
    } else {
      // Related: This contact is related to the selected contact
      createContactConnection.mutate({
        sourceContactId: contact.id,
        targetContactId: selectedContact.id,
        relationshipType: "related",
      });
    }

    setSlideoverOpen(false);
    setConnectionType(null);
  };

  const handleDeleteConnection = () => {
    if (!connectionToDelete) return;

    deleteContactConnection.mutate({
      id: connectionToDelete.id,
    });
    setConnectionToDelete(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Typography tag="h3" className="text-lg font-semibold">
          Contact connections
        </Typography>
      </div>

      {/* Parent Connections */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Typography
            tag="h4"
            className="text-muted-foreground text-sm font-medium"
          >
            Reports to
          </Typography>
          {hasCreatePermission && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddParentConnection}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add
            </Button>
          )}
        </div>
        {parentConnections.length > 0 && (
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            {parentConnections.map((connection) => (
              <div
                key={connection.id}
                className="group hover:bg-accent relative flex cursor-pointer items-center gap-3 rounded-md border p-3 transition-colors"
                onClick={() => {
                  void navigate({
                    to: `/contacts/${connection.contact.id}`,
                  });
                }}
              >
                {connection.contact.type === "individual" ? (
                  <User className="text-muted-foreground h-5 w-5 flex-shrink-0" />
                ) : (
                  <Building2 className="text-muted-foreground h-5 w-5 flex-shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <Typography className="truncate text-sm font-medium">
                    {formatContactsName(connection.contact)}
                  </Typography>
                  <Typography className="text-muted-foreground text-xs">
                    {connection.contact.type === "individual"
                      ? "Individual"
                      : "Organization"}
                  </Typography>
                </div>
                {hasDeletePermission && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConnectionToDelete({
                        id: connection.id,
                      });
                    }}
                  >
                    <Trash2 className="text-destructive h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
        {parentConnections.length === 0 && (
          <div className="text-muted-foreground py-4 text-center text-sm">
            No parent connections yet
          </div>
        )}
      </div>

      {/* Child Connections */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Typography
            tag="h4"
            className="text-muted-foreground text-sm font-medium"
          >
            Manages
          </Typography>
          {hasCreatePermission && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddChildConnection}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add
            </Button>
          )}
        </div>
        {childConnections.length > 0 && (
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            {childConnections.map((connection) => (
              <div
                key={connection.id}
                className="group hover:bg-accent relative flex cursor-pointer items-center gap-3 rounded-md border p-3 transition-colors"
                onClick={() => {
                  void navigate({
                    to: `/contacts/${connection.contact.id}`,
                  });
                }}
              >
                {connection.contact.type === "individual" ? (
                  <User className="text-muted-foreground h-5 w-5 flex-shrink-0" />
                ) : (
                  <Building2 className="text-muted-foreground h-5 w-5 flex-shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <Typography className="truncate text-sm font-medium">
                    {formatContactsName(connection.contact)}
                  </Typography>
                  <Typography className="text-muted-foreground text-xs">
                    {connection.contact.type === "individual"
                      ? "Individual"
                      : "Organization"}
                  </Typography>
                </div>
                {hasDeletePermission && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConnectionToDelete({
                        id: connection.id,
                      });
                    }}
                  >
                    <Trash2 className="text-destructive h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
        {childConnections.length === 0 && (
          <div className="text-muted-foreground py-4 text-center text-sm">
            No child connections yet
          </div>
        )}
      </div>

      {/* Related Connections */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Typography
            tag="h4"
            className="text-muted-foreground text-sm font-medium"
          >
            Related to
          </Typography>
          {hasCreatePermission && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddRelatedConnection}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add
            </Button>
          )}
        </div>
        {relatedConnections.length > 0 && (
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            {relatedConnections.map((connection) => (
              <div
                key={connection.id}
                className="group hover:bg-accent relative flex cursor-pointer items-center gap-3 rounded-md border p-3 transition-colors"
                onClick={() => {
                  void navigate({
                    to: `/contacts/${connection.contact.id}`,
                  });
                }}
              >
                {connection.contact.type === "individual" ? (
                  <User className="text-muted-foreground h-5 w-5 flex-shrink-0" />
                ) : (
                  <Building2 className="text-muted-foreground h-5 w-5 flex-shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <Typography className="truncate text-sm font-medium">
                    {formatContactsName(connection.contact)}
                  </Typography>
                  <Typography className="text-muted-foreground text-xs">
                    {connection.contact.type === "individual"
                      ? "Individual"
                      : "Organization"}
                  </Typography>
                </div>
                {hasDeletePermission && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConnectionToDelete({
                        id: connection.id,
                      });
                    }}
                  >
                    <Trash2 className="text-destructive h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
        {relatedConnections.length === 0 && (
          <div className="text-muted-foreground py-4 text-center text-sm">
            No related connections yet
          </div>
        )}
      </div>

      <SelectContactModal
        open={slideoverOpen}
        setOpen={setSlideoverOpen}
        onSelectContact={handleSelectContact}
        excludeContactIds={connectedContactIds}
        title={
          connectionType === "parent"
            ? "Select parent contact"
            : connectionType === "child"
              ? "Select child contact"
              : "Select related contact"
        }
        description={
          connectionType === "parent"
            ? `Select a contact that ${formatContactsName(contact)} reports to`
            : connectionType === "child"
              ? `Select a contact that reports to ${formatContactsName(contact)}`
              : `Select a contact that is related to ${formatContactsName(contact)}`
        }
      />

      <ActionConfirmationModal
        open={!!connectionToDelete}
        setOpen={(open) => {
          if (!open) {
            setConnectionToDelete(null);
          }
        }}
        title="Delete contact connection"
        description="Are you sure you want to delete this contact connection? This action cannot be undone."
        afterActionFn={handleDeleteConnection}
      />
    </div>
  );
}

export default function ContactConnectionsSection(
  props: ContactConnectionsSectionProps,
) {
  return (
    <ErrorBoundary fallback={<ServerErrorFallbackCard />}>
      <ContactConnectionsSectionDangerous {...props} />
    </ErrorBoundary>
  );
}
